import React, { useState, useEffect } from 'react';
import { useCustomNavigate } from '../../hooks/useCustomNavigate';
import { CustomLink } from '../../components/CustomLink';
import { Button } from '../../components/Button';
import { Mail, Lock, LogIn, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/authService';

function Login() {
  const navigate = useCustomNavigate();
  const { login, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });
  
  // Проверяем авторизацию при загрузке компонента (только если не идет процесс логина)
  useEffect(() => {
    // Не перенаправляем во время процесса логина или загрузки
    if (!isAuthLoading && !isLoading && isAuthenticated) {
      // Если пользователь уже авторизован, перенаправляем на главную страницу
      navigate(`/studio`);
    }
  }, [isAuthenticated, isAuthLoading, isLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setFormError(null);

    try {
      // Отправляем значение identifier как email
      const response = await authService.login(formData.identifier, formData.password);
      
      if (response.success === false) {
        setFormError(response.error || 'Неверный логин или пароль');
        setIsLoading(false);
        return;
      }

      if (!response.token) {
        setFormError('Ошибка авторизации: токен не получен');
        setIsLoading(false);
        return;
      }

      login(response.token);
      
      // Используем абсолютный URL для гарантированного обновления
      const baseUrl = window.location.origin;
      window.location.href = `${baseUrl}/studio`;
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Неверный логин или пароль';
      setFormError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (response) => {
      try {
        const result = await authService.googleAuth(response.access_token);
        if (result.token) {
          login(result.token);
          // Используем абсолютный URL для гарантированного обновления
          const baseUrl = window.location.origin;
          window.location.href = `${baseUrl}/studio`;
        } else {
          navigate('/auth/setup-nickname');
        }
      } catch (error: any) {
        setFormError('Ошибка при авторизации через Google');
      }
    },
    onError: () => {
      setFormError('Ошибка при авторизации через Google');
    }
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 transition-colors duration-200">
      <div className="fixed top-4 right-4">
        <button
          onClick={() => document.documentElement.classList.toggle('dark')}
          className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          aria-label="Toggle theme"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-800 dark:text-gray-200">
            <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"></path>
          </svg>
        </button>
      </div>
      <div className="max-w-md mx-auto px-4 py-12">
        <div className="mb-4">
          <CustomLink
            to="/"
            className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            ← Вернуться на главную
          </CustomLink>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            Вход в систему
          </h2>

          {formError && (
            <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mr-2" />
                <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email или логин
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={formData.identifier}
                  onChange={(e) => {
                    setFormData({ ...formData, identifier: e.target.value });
                    // Очищаем ошибку только при начале ввода
                    if (formError) {
                      setFormError(null);
                    }
                  }}
                  className={`w-full pl-10 p-3 rounded-lg border ${
                    formError ? 'border-red-300 dark:border-red-700' : 'border-gray-200 dark:border-gray-700'
                  } bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 ${
                    formError ? 'focus:ring-red-500/25' : 'focus:ring-blue-500/25'
                  }`}
                  placeholder="Введите email или логин"
                  required
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Пароль
                </label>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => {
                    setFormData({ ...formData, password: e.target.value });
                    // Очищаем ошибку только при начале ввода
                    if (formError) {
                      setFormError(null);
                    }
                  }}
                  className={`w-full pl-10 pr-10 p-3 rounded-lg border ${
                    formError ? 'border-red-300 dark:border-red-700' : 'border-gray-200 dark:border-gray-700'
                  } bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 ${
                    formError ? 'focus:ring-red-500/25' : 'focus:ring-blue-500/25'
                  }`}
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div className="flex justify-end">
                <CustomLink
                    to="/auth/forgot-password"
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                  Забыли пароль?
                </CustomLink>
              </div>
            </div>
            <Button
              type="submit"
              disabled={isLoading}
              variant="primary-filled"
              fullWidth
              className="flex items-center justify-center"
            >
              <LogIn className="w-5 h-5 mr-2" />
              {isLoading ? 'Вход...' : 'Войти'}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                  Или войдите через
                </span>
              </div>
            </div>

            <Button
              onClick={() => googleLogin()}
              disabled={true}
              variant="secondary"
              fullWidth
              className="mt-4 flex items-center justify-center"
            >
              <img
                src="https://www.google.com/favicon.ico"
                alt="Google"
                className="w-5 h-5 mr-2"
              />
              Google (временно недоступно)
            </Button>
          </div>

          <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
            Нет аккаунта?{' '}
            <CustomLink
              to="/auth/register"
              className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
            >
              Зарегистрироваться
            </CustomLink>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
